/**
 * Session Policy Store — P28 Phase C-2 (canon 28 §2.3, migration 598)
 *
 * `session_policies` is the sole home of Session Policy data: the behavioral
 * defaults bundle per cascade level (workspace | workflow | cycle | track).
 * The legacy surfaces (default_* columns on workspaces/degap_cycles/tracks,
 * degap_cycles.metadata.sessionDefaults policy keys, tracks.metadata
 * allowSession* flags) were retired in migration 598.
 *
 * Wiring choice — decoration, not resolver rewrite: this store is the single
 * read/write path for policy rows, and the entity stores (trackStore,
 * tenantStore, degapProcessStore) call the fetch helpers here during their own
 * fetches, then merge ("decorate") the policy values onto the existing domain
 * fields (track.defaultTimingMode, workspace.defaultEntryMode, synthesized
 * cycle.metadata.sessionDefaults / track.metadata.allowSession* keys). Every
 * existing reader — effectiveConfigResolver, bindingConflictDetector,
 * trackConditionsSummary, the settings UIs — keeps working unchanged.
 *
 * Per Build Contract: Database is the system of record. Every write goes
 * through upsertPolicy to the set_session_policy RPC (migration 1012), which
 * takes the workspace from the target, checks the person's right at that level
 * and logs the change. Members read their workspace's rows; nobody writes them
 * directly.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

/** 'session' added by migration 891 (S-33) for a single session's switches. */
export type SessionPolicyScopeKind = 'workspace' | 'workflow' | 'cycle' | 'track' | 'session';

export interface SessionPolicy {
  id: string;
  scopeKind: SessionPolicyScopeKind;
  scopeId: string;
  workspaceId: string | null;
  timingMode: 'live' | 'async' | null;
  entryMode: 'facilitator' | 'participants' | null;
  defaultFacilitatorId: string | null;
  allowSessionOverride: boolean | null;
  allowSessionItemCreation: boolean | null;
  allowSessionValueCreation: boolean | null;
  allowSessionValueOverride: boolean | null;
  /** S-33 (mig 891): may the ranking order and cut line change after the run? null = inherit. */
  allowRankOrderChange: boolean | null;
  /** S-2 (mig 973): when the host's timer reaches zero, answers lock or the host is told. null = inherit. */
  timerAtZero: TimerAtZero | null;
  /** S-3 (mig 973): whether names are shown, hidden on screens, or withheld from the data too. null = inherit. */
  anonymity: RoomAnonymity | null;
}

/** S-2 (mig 973): what the room's timer does at zero. */
export type TimerAtZero = 'lock' | 'tell';
/** S-3 (mig 973): how a session shows who said what. */
export type RoomAnonymity = 'shown' | 'hidden' | 'withheld';

export interface UpsertSessionPolicyInput {
  scopeKind: SessionPolicyScopeKind;
  scopeId: string;
  /** Omit a field to leave it untouched on an existing row; pass null to clear it. */
  timingMode?: 'live' | 'async' | null;
  entryMode?: 'facilitator' | 'participants' | null;
  defaultFacilitatorId?: string | null;
  allowSessionOverride?: boolean | null;
  allowSessionItemCreation?: boolean | null;
  allowSessionValueCreation?: boolean | null;
  allowSessionValueOverride?: boolean | null;
  allowRankOrderChange?: boolean | null;
  timerAtZero?: TimerAtZero | null;
  anonymity?: RoomAnonymity | null;
}

interface SessionPolicyRow {
  id: string;
  scope_kind: string;
  scope_id: string;
  workspace_id: string | null;
  timing_mode: string | null;
  entry_mode: string | null;
  default_facilitator_id: string | null;
  allow_session_override: boolean | null;
  allow_session_item_creation: boolean | null;
  allow_session_value_creation: boolean | null;
  allow_session_value_override: boolean | null;
  /** untyped until db:types after prod apply (mig 891) */
  allow_rank_order_change?: boolean | null;
  timer_at_zero?: string | null;
  anonymity?: string | null;
}

function policyKey(scopeKind: SessionPolicyScopeKind, scopeId: string): string {
  return `${scopeKind}:${scopeId}`;
}

function rowToDomain(row: SessionPolicyRow): SessionPolicy {
  return {
    id: row.id,
    scopeKind: row.scope_kind as SessionPolicyScopeKind,
    scopeId: row.scope_id,
    workspaceId: row.workspace_id,
    timingMode: row.timing_mode === 'live' || row.timing_mode === 'async' ? row.timing_mode : null,
    entryMode: row.entry_mode === 'facilitator' || row.entry_mode === 'participants' ? row.entry_mode : null,
    defaultFacilitatorId: row.default_facilitator_id || null,
    allowSessionOverride: row.allow_session_override,
    allowSessionItemCreation: row.allow_session_item_creation,
    allowSessionValueCreation: row.allow_session_value_creation,
    allowSessionValueOverride: row.allow_session_value_override,
    allowRankOrderChange: typeof row.allow_rank_order_change === 'boolean' ? row.allow_rank_order_change : null,
    timerAtZero: row.timer_at_zero === 'lock' || row.timer_at_zero === 'tell' ? row.timer_at_zero : null,
    anonymity:
      row.anonymity === 'shown' || row.anonymity === 'hidden' || row.anonymity === 'withheld' ? row.anonymity : null,
  };
}

// ============================================================================
// Store
// ============================================================================

interface SessionPolicyStore {
  /** Policies keyed `${scope_kind}:${scope_id}`. */
  policies: Record<string, SessionPolicy>;
  isLoading: boolean;
  error: string | null;

  /** Fetch every policy row for a workspace (plus NULL-workspace rows for Unbound scopes). */
  fetchPoliciesForWorkspace: (workspaceId: string) => Promise<void>;
  /** Fetch policy rows for specific scopes and return them keyed by scope_id. */
  fetchPoliciesForScopes: (
    scopeKind: SessionPolicyScopeKind,
    scopeIds: string[]
  ) => Promise<Map<string, SessionPolicy>>;
  /** Upsert (onConflict scope_kind,scope_id). Only provided fields are written. */
  upsertPolicy: (input: UpsertSessionPolicyInput) => Promise<SessionPolicy | null>;

  getPolicy: (scopeKind: SessionPolicyScopeKind, scopeId: string) => SessionPolicy | undefined;
  clearError: () => void;
}

/** Ids per session_policies request; keeps the query string near 4 KB. */
const SCOPE_IDS_PER_REQUEST = 100;

export const useSessionPolicyStore = create<SessionPolicyStore>()((set, get) => ({
  policies: {},
  isLoading: false,
  error: null,

  fetchPoliciesForWorkspace: async (workspaceId: string): Promise<void> => {
    if (!isSupabaseAvailable()) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('session_policies')
        .select('*')
        .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);

      if (error) {
        console.error('[SessionPolicyStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      set((state) => {
        const next = { ...state.policies };
        for (const row of (data ?? []) as SessionPolicyRow[]) {
          const policy = rowToDomain(row);
          next[policyKey(policy.scopeKind, policy.scopeId)] = policy;
        }
        return { policies: next, isLoading: false, error: null };
      });
    } catch (err) {
      console.error('[SessionPolicyStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  fetchPoliciesForScopes: async (
    scopeKind: SessionPolicyScopeKind,
    scopeIds: string[]
  ): Promise<Map<string, SessionPolicy>> => {
    const result = new Map<string, SessionPolicy>();
    if (scopeIds.length === 0) return result;
    if (!isSupabaseAvailable()) return result;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return result;

    try {
      // The ids ride in the URL. A workspace with hundreds of tracks made one
      // request too long for the gateway (about 39 characters an id; staging's
      // walkthrough workspace has 649 tracks, a 25 KB query string), so ask in
      // batches small enough for any gateway and run them side by side.
      const batches: string[][] = [];
      for (let i = 0; i < scopeIds.length; i += SCOPE_IDS_PER_REQUEST) {
        batches.push(scopeIds.slice(i, i + SCOPE_IDS_PER_REQUEST));
      }
      const responses = await Promise.all(
        batches.map((ids) =>
          supabase
            .from('session_policies')
            .select('*')
            .eq('scope_kind', scopeKind)
            .in('scope_id', ids),
        ),
      );
      const failed = responses.find((r) => r.error);
      if (failed?.error) {
        console.error('[SessionPolicyStore] Scope fetch error:', failed.error);
        set({ error: failed.error.message });
        return result;
      }
      const rows = responses.flatMap((r) => (r.data ?? []) as SessionPolicyRow[]);

      set((state) => {
        const next = { ...state.policies };
        for (const row of rows) {
          const policy = rowToDomain(row);
          next[policyKey(policy.scopeKind, policy.scopeId)] = policy;
          result.set(policy.scopeId, policy);
        }
        return { policies: next };
      });
      return result;
    } catch (err) {
      console.error('[SessionPolicyStore] Scope fetch exception:', err);
      set({ error: String(err) });
      return result;
    }
  },

  upsertPolicy: async (input: UpsertSessionPolicyInput): Promise<SessionPolicy | null> => {
    if (!isSupabaseAvailable()) {
      set({ error: 'Supabase not available' });
      return null;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Supabase client not available' });
      return null;
    }

    try {
      const changes: Record<string, unknown> = {};
      if (input.timingMode !== undefined) changes.timing_mode = input.timingMode;
      if (input.entryMode !== undefined) changes.entry_mode = input.entryMode;
      if (input.defaultFacilitatorId !== undefined) {
        changes.default_facilitator_id = input.defaultFacilitatorId || null;
      }
      if (input.allowSessionOverride !== undefined) changes.allow_session_override = input.allowSessionOverride;
      if (input.allowSessionItemCreation !== undefined) changes.allow_session_item_creation = input.allowSessionItemCreation;
      if (input.allowSessionValueCreation !== undefined) changes.allow_session_value_creation = input.allowSessionValueCreation;
      if (input.allowSessionValueOverride !== undefined) changes.allow_session_value_override = input.allowSessionValueOverride;
      if (input.allowRankOrderChange !== undefined) changes.allow_rank_order_change = input.allowRankOrderChange;
      if (input.timerAtZero !== undefined) changes.timer_at_zero = input.timerAtZero;
      if (input.anonymity !== undefined) changes.anonymity = input.anonymity;

      const { data, error } = await supabase.rpc('set_session_policy', {
        p_scope_kind: input.scopeKind,
        p_scope_id: input.scopeId,
        p_changes: changes,
      });

      if (error) {
        console.error('[SessionPolicyStore] Save error:', error);
        set({ error: error.message });
        return null;
      }
      const saved = data as { success: boolean; error?: string; policy?: SessionPolicyRow } | null;
      if (!saved?.success || !saved.policy) {
        // The server's own words: they say what the person is missing.
        set({ error: saved?.error ?? 'The setting could not be saved.' });
        return null;
      }

      const policy = rowToDomain(saved.policy);
      set((state) => ({
        policies: { ...state.policies, [policyKey(policy.scopeKind, policy.scopeId)]: policy },
        error: null,
      }));
      return policy;
    } catch (err) {
      console.error('[SessionPolicyStore] Save exception:', err);
      set({ error: String(err) });
      return null;
    }
  },

  getPolicy: (scopeKind, scopeId) => get().policies[policyKey(scopeKind, scopeId)],

  clearError: () => set({ error: null }),
}));

// ============================================================================
// Decoration helpers (Phase C-2)
//
// Merge policy values onto the legacy domain fields so existing readers keep
// working. All are pure; the entity stores call them after their own fetches.
// ============================================================================

/** Track-shaped decoration target (kept structural to avoid import cycles). */
interface TrackPolicyTarget {
  defaultTimingMode: 'live' | 'async' | null;
  defaultEntryMode: 'facilitator' | 'participants' | null;
  defaultFacilitatorId: string | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Merge a 'track' policy row onto the track domain object: the three
 * default* fields plus the four allowSession* flags synthesized into
 * `metadata` (readers like SessionDetail/TrackDetail/bindingConflictDetector
 * check `track.metadata.allowSessionOverride` etc.).
 */
export function decorateTrackWithPolicy<T extends TrackPolicyTarget>(
  track: T,
  policy: SessionPolicy | undefined
): T {
  if (!policy) return track;
  const allowFlags: Record<string, unknown> = {};
  if (policy.allowSessionOverride !== null) allowFlags.allowSessionOverride = policy.allowSessionOverride;
  if (policy.allowSessionItemCreation !== null) allowFlags.allowSessionItemCreation = policy.allowSessionItemCreation;
  if (policy.allowSessionValueCreation !== null) allowFlags.allowSessionValueCreation = policy.allowSessionValueCreation;
  if (policy.allowSessionValueOverride !== null) allowFlags.allowSessionValueOverride = policy.allowSessionValueOverride;
  return {
    ...track,
    defaultTimingMode: policy.timingMode ?? track.defaultTimingMode,
    defaultEntryMode: policy.entryMode ?? track.defaultEntryMode,
    defaultFacilitatorId: policy.defaultFacilitatorId ?? track.defaultFacilitatorId,
    metadata:
      Object.keys(allowFlags).length > 0
        ? { ...(track.metadata ?? {}), ...allowFlags }
        : track.metadata,
  };
}

/** Workspace-shaped decoration target. */
interface WorkspacePolicyTarget {
  defaultTimingMode?: string | null;
  defaultEntryMode?: string | null;
  defaultFacilitatorId?: string | null;
}

/** Merge a 'workspace' policy row onto the workspace domain object. */
export function decorateWorkspaceWithPolicy<T extends WorkspacePolicyTarget>(
  workspace: T,
  policy: SessionPolicy | undefined
): T {
  if (!policy) return workspace;
  return {
    ...workspace,
    defaultTimingMode: policy.timingMode ?? workspace.defaultTimingMode ?? null,
    defaultEntryMode: policy.entryMode ?? workspace.defaultEntryMode ?? null,
    defaultFacilitatorId: policy.defaultFacilitatorId ?? workspace.defaultFacilitatorId ?? null,
  };
}

/** Cycle-shaped decoration target. */
interface CyclePolicyTarget {
  metadata?: Record<string, unknown>;
}

/**
 * Merge a 'cycle' policy row into `metadata.sessionDefaults` (synthesized) so
 * readers of the legacy shape (cycleLayerFromCycle, CycleSessionDefaultsSection,
 * bindingConflictDetector) keep working. Non-policy sessionDefaults keys
 * (resultResolution, defaultDurationMinutes, catchballEnabled) still live in
 * the persisted cycle metadata and are preserved.
 */
export function decorateCycleWithPolicy<T extends CyclePolicyTarget>(
  cycle: T,
  policy: SessionPolicy | undefined
): T {
  if (!policy) return cycle;
  const policyDefaults: Record<string, unknown> = {};
  if (policy.timingMode !== null) policyDefaults.timingMode = policy.timingMode;
  if (policy.entryMode !== null) policyDefaults.entryMode = policy.entryMode;
  if (policy.defaultFacilitatorId !== null) policyDefaults.defaultFacilitatorId = policy.defaultFacilitatorId;
  if (Object.keys(policyDefaults).length === 0) return cycle;
  const existingDefaults =
    cycle.metadata && typeof cycle.metadata.sessionDefaults === 'object' && cycle.metadata.sessionDefaults !== null
      ? (cycle.metadata.sessionDefaults as Record<string, unknown>)
      : {};
  return {
    ...cycle,
    metadata: {
      ...(cycle.metadata ?? {}),
      sessionDefaults: { ...existingDefaults, ...policyDefaults },
    },
  };
}

export default useSessionPolicyStore;
